import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuthStore, useCartStore } from '../lib/store';
import { formatPrice } from '../lib/utils';
import { Loader2, Lock, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuthStore();
  const { items, getTotal } = useCartStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const total = getTotal();

  if (!isAuthenticated) {
    navigate('/');
    return null;
  }

  if (items.length === 0) {
    navigate('/products');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const formData = new FormData(e.target);
    
    try {
      const res = await axios.post(
        `${API}/checkout/create-session`,
        {
          origin_url: window.location.origin,
          shipping_name: formData.get('name'),
          shipping_address: formData.get('address'),
          shipping_city: formData.get('city'),
          shipping_postal: formData.get('postal'),
          shipping_country: formData.get('country'),
        },
        { headers: getAuthHeader() }
      );
      
      // Redirect to Stripe checkout
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setIsLoading(false);
    }
  };

  return (
    <div className="paper-texture min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-4xl font-bold text-secondary mb-8" data-testid="checkout-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Shipping Form */}
          <div>
            <div className="bg-white rounded-xl border p-8">
              <h2 className="font-heading text-2xl font-semibold text-secondary mb-6">Shipping Information</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" name="name" placeholder="John Doe" required data-testid="shipping-name" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" name="address" placeholder="123 Main St" required data-testid="shipping-address" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" name="city" placeholder="London" required data-testid="shipping-city" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal">Postal Code</Label>
                    <Input id="postal" name="postal" placeholder="SW1A 1AA" required data-testid="shipping-postal" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" name="country" placeholder="United Kingdom" required data-testid="shipping-country" />
                </div>

                {error && <p className="text-destructive text-sm" data-testid="checkout-error">{error}</p>}

                <Button type="submit" size="lg" className="w-full" disabled={isLoading} data-testid="pay-button">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Pay {formatPrice(total)}
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Secure payment powered by Stripe
                </p>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-surface rounded-xl border p-8 sticky top-24">
              <h2 className="font-heading text-2xl font-semibold text-secondary mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-4" data-testid={`order-item-${item.id}`}>
                    <img src={item.product_image} alt={item.product_name} className="w-16 h-16 object-cover rounded-md" />
                    <div className="flex-1">
                      <h4 className="font-medium text-secondary">{item.product_name}</h4>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                    </div>
                    <span className="font-medium text-secondary">{formatPrice(item.product_price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-secondary pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary" data-testid="order-total">{formatPrice(total)}</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                <div className="flex items-center gap-2 text-primary">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="font-medium">Free shipping on all orders</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
